import React, { useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';

import { Button } from 'components/Button';
import CustomModal from 'components/CustomModal';
import { useStore } from 'utils/hooks/store';

import FolderTreeSelectItem from '../FolderTreePicker/FolderTreeSelectItem';
import { FolderPath, TreeContainer } from '../FolderTreePicker/styles';
import { Description, ModalContent, PathValue } from './styles';

interface MoveToFolderModalProps {
  open: boolean;
  documentName: string;
  currentFolderId?: string | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (folderId: string) => void;
}

const MoveToFolderModal: React.FC<MoveToFolderModalProps> = ({
  open,
  documentName,
  currentFolderId = null,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const { folders: foldersStore } = useStore();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(currentFolderId);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());

  const isSubmitDisabled = loading || !selectedFolderId || selectedFolderId === currentFolderId;

  const selectedFolderPath = useMemo(() => {
    if (!selectedFolderId) return '';
    return foldersStore.getFolderPath(selectedFolderId);
  }, [selectedFolderId, foldersStore]);

  const handleSubmit = (): void => {
    if (selectedFolderId) {
      onSubmit(selectedFolderId);
    }
  };

  const handleClose = (): void => {
    if (!loading) {
      setSelectedFolderId(currentFolderId);
      onClose();
    }
  };

  const handleSelect = useCallback((folderId: string): void => {
    setSelectedFolderId(folderId);
  }, []);

  const handleToggleExpand = useCallback((folderId: string): void => {
    setExpandedFolderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  return (
    <CustomModal
      openModal={open}
      onClose={handleClose}
      modalTitle="Move to Folder"
      modalWidth="sm"
      cancelButton={
        <Button disabled={loading} onClick={handleClose} variant="outlined" size="large">
          Cancel
        </Button>
      }
      submitButton={
        <Button onClick={handleSubmit} disabled={isSubmitDisabled} variant="contained" size="large">
          Move
        </Button>
      }
    >
      <ModalContent>
        <Description>Select a destination folder for &quot;{documentName}&quot;</Description>
        <TreeContainer>
          {foldersStore.folders.map((folder) => (
            <FolderTreeSelectItem
              key={folder.uuid}
              folder={folder}
              depth={0}
              selectedFolderId={selectedFolderId}
              expandedFolderIds={expandedFolderIds}
              onSelect={handleSelect}
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </TreeContainer>
        {selectedFolderPath && (
          <FolderPath>
            New folder path: <PathValue>{selectedFolderPath}</PathValue>
          </FolderPath>
        )}
      </ModalContent>
    </CustomModal>
  );
};

export default observer(MoveToFolderModal);
